/*
 * Everything that reads a notification's payload. Nothing here imports from notifications.model:
 * that file imports the `create*Summary` functions from here, and a runtime import back would make the load
 * order decide whether its enums exist yet.
 */

export interface BuildMigrationNotificationPayload {
  sourceRootBuildTag: string
  targetRootBuildTag: string
  totalMigrationInstances: number
  totalFailedMigrationInstances?: number
}

/** What an export, of a version or a build, was run with. */
export interface ExportNotificationPayload {
  flowxVersion: string
  includeBinaries: boolean
}

/** One resource the target FlowX version does not support, left out of the exported archive. */
export interface ExcludedResource {
  resourceType: string
  resourceName: string
}

/**
 * The `details` of an export, of a version or a build, that finished with a warning: every resource
 * it left out.
 */
export interface ExportNotificationDetails {
  resources: ExcludedResource[]
}

export enum SummarySegmentType {
  TEXT = 'TEXT',
  ID = 'ID', // Truncated on the card, in full in its tooltip and in search.
  COPYABLE_ID = 'COPYABLE_ID', // An ID labelled `ID:`, with a button that copies it.
  ARROW = 'ARROW',
  DOT = 'DOT',
}

export type SummarySegment =
  | {
      type: SummarySegmentType.TEXT | SummarySegmentType.ID | SummarySegmentType.COPYABLE_ID
      text: string
    }
  | { type: SummarySegmentType.ARROW | SummarySegmentType.DOT; text?: undefined }

/**
 * The payload's facts as the card lists them (`1.6.2 → 1.6.3 · 128 instances`). Empty when the
 * payload has nothing to show.
 */
export type NotificationSummary = SummarySegment[]

/** `countNoun` labels the count; it comes from the notification's type and status. */
export const createBuildMigrationSummary = (
  countNoun: string | undefined,
  {
    sourceRootBuildTag,
    targetRootBuildTag,
    totalMigrationInstances,
    totalFailedMigrationInstances,
  }: BuildMigrationNotificationPayload
): NotificationSummary => {
  const migrated = totalMigrationInstances - (totalFailedMigrationInstances ?? 0)

  return sourceRootBuildTag && targetRootBuildTag
    ? [
        textSegment(sourceRootBuildTag),
        ARROW_SEGMENT,
        textSegment(targetRootBuildTag),
        ...(countNoun ? [DOT_SEGMENT, textSegment(`${migrated} ${countNoun}`)] : []),
      ]
    : []
}

/** The envelope fields that name a build. */
interface BuildIdentity {
  appVersionId: string
  branchName?: string
  buildTag?: string
}

// A build reads as its branch and version without a tag. A build import's summary is this alone.
export const buildNameSegments = ({
  appVersionId,
  branchName,
  buildTag,
}: BuildIdentity): SummarySegment[] =>
  buildTag
    ? [textSegment(`Build ${buildTag}`)]
    : [...(branchName ? [textSegment(branchName)] : []), idSegment(appVersionId)]

const exportDetailSegments = ({
  flowxVersion,
  includeBinaries,
}: ExportNotificationPayload): SummarySegment[] => [
  ...(flowxVersion ? [DOT_SEGMENT, textSegment(`Target version ${flowxVersion}`)] : []),
  DOT_SEGMENT,
  textSegment(includeBinaries ? 'media included' : 'media excluded'),
]

export const createBuildExportSummary = (
  build: BuildIdentity,
  payload: ExportNotificationPayload
): NotificationSummary => [...buildNameSegments(build), ...exportDetailSegments(payload)]

export const createVersionExportSummary = (
  { appVersionId }: { appVersionId: string },
  payload: ExportNotificationPayload
): NotificationSummary => [copyableIdSegment(appVersionId), ...exportDetailSegments(payload)]

export const createVersionImportSummary = ({
  appVersionId,
}: {
  appVersionId: string
}): NotificationSummary => [copyableIdSegment(appVersionId)]

const textSegment = (text: string): SummarySegment => ({ type: SummarySegmentType.TEXT, text })

const idSegment = (text: string): SummarySegment => ({ type: SummarySegmentType.ID, text })

const copyableIdSegment = (text: string): SummarySegment => ({
  type: SummarySegmentType.COPYABLE_ID,
  text,
})

const ARROW_SEGMENT: SummarySegment = { type: SummarySegmentType.ARROW }

const DOT_SEGMENT: SummarySegment = { type: SummarySegmentType.DOT }
